import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';
import { formatDateTime } from '../shared/formatting';
import { AdminImportAuditSection } from './adminImportAuditSection';

type Locale = 'hr' | 'en';

type InboxRow = {
    id: number;
    email_message_id: string | null;
    sender_email: string | null;
    sender_name: string | null;
    subject: string | null;
    company_id: number | null;
    company_code: string | null;
    company_name: string | null;
    filename: string;
    content_type: string | null;
    size_bytes: number | null;
    classifier_guess: string | null;
    classifier_confidence: number | null;
    classifier_reasoning: string | null;
    status: 'pending' | 'routed' | 'auto_routed' | 'rejected';
    routed_to_module: string | null;
    routed_at: string | null;
    rejection_reason: string | null;
    imported_batch_id: number | null;
    import_status: 'pending' | 'success' | 'failed' | 'awaiting_manual' | null;
    import_error: string | null;
    import_summary?: string | null;
    import_finished_at: string | null;
    created_at: string;
};

type CompanyOpt = { id: number; code: string; name: string };

type InboxPayload = {
    rows: InboxRow[];
    known_modules: Record<string, string>;
    companies: CompanyOpt[];
    counts: Record<string, number>;
};

type Route = {
    id: number;
    module_key: string;
    event_kind: string;
    recipients: string;
    is_active: boolean | 0 | 1;
    created_at: string;
    updated_at: string;
};

type RoutesPayload = {
    routes: Route[];
    known_modules: Record<string, string>;
    known_events: Record<string, string>;
};

type Tab = 'inbox' | 'audit' | 'routes';

export function AdminIngestSection({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [tab, setTab] = useState<Tab>('inbox');

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📨</div>
                <div className="bi-module-hero-text">
                    <h1>142 · {t('Centralni ingest (obrada@aurodomus.eu)', 'Central ingest (obrada@aurodomus.eu)')}</h1>
                    <p className="muted">
                        {t(
                            'Sve datoteke koje stignu na obrada@ klasifikator pokušava prepoznati i poslati u pravi modul. Što ne prepozna ide ovdje. Notification routing po modulu kaže tko dobije obavijest o čemu.',
                            'Files arriving at obrada@ are classified and routed automatically; unmatched ones land here. Notification routing per module decides who gets which alert.',
                        )}
                    </p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.25)', paddingBottom: 10 }}>
                {([
                    { id: 'inbox' as Tab, icon: '📥', label: t('Neraspoređeni fajlovi', 'Unrouted files') },
                    { id: 'audit' as Tab, icon: '📊', label: t('Praćenje importa', 'Import audit') },
                    { id: 'routes' as Tab, icon: '🔔', label: t('Notification routing', 'Notification routing') },
                ]).map((cfg) => {
                    const active = tab === cfg.id;
                    return (
                        <button
                            key={cfg.id}
                            type="button"
                            onClick={() => setTab(cfg.id)}
                            style={{
                                padding: '8px 14px',
                                borderRadius: 8,
                                border: active ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                                background: active ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                                color: active ? '#fffbeb' : 'var(--ad-ink)',
                                fontWeight: 600,
                                fontSize: '0.88rem',
                                cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 8,
                            }}
                        >
                            <span>{cfg.icon}</span>
                            <span>{cfg.label}</span>
                        </button>
                    );
                })}
            </div>

            {tab === 'inbox' ? <InboxTab token={token} locale={locale} /> : null}
            {tab === 'audit' ? <AdminImportAuditSection token={token} locale={locale} /> : null}
            {tab === 'routes' ? <RoutesTab token={token} locale={locale} /> : null}
        </section>
    );
}

function InboxTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<InboxPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState<'pending' | 'routed' | 'auto_routed' | 'rejected' | 'all'>('pending');
    const [routePick, setRoutePick] = useState<Record<number, string>>({});
    const [companyPick, setCompanyPick] = useState<Record<number, number>>({});

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const r = await api<InboxPayload>(`/admin/ingest/inbox?status=${statusFilter}`, token);
            setData(r);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, statusFilter]);

    useEffect(() => { void load(); }, [load]);

    async function routeOne(id: number, currentCompanyId: number | null, classifierGuess?: string | null) {
        // Fallback chain mora biti identičan kao u <select value=...>: user pick → classifier guess → null
        const mod = routePick[id] ?? classifierGuess ?? null;
        if (!mod) {
            alert(t('Odaberi modul prvo.', 'Pick a module first.'));
            return;
        }
        const cmp = companyPick[id] ?? currentCompanyId;
        if (!cmp) {
            alert(t('Odaberi firmu prvo.', 'Pick a company first.'));
            return;
        }
        try {
            await mutate(`/admin/ingest/inbox/${id}/route`, token, 'POST', { module: mod, company_id: cmp });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    async function rejectOne(id: number) {
        const reason = prompt(t('Razlog odbacivanja:', 'Rejection reason:'), '');
        if (reason === null) return;
        try {
            await mutate(`/admin/ingest/inbox/${id}/reject`, token, 'POST', { reason });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    if (loading && !data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    return (
        <>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {(['pending', 'auto_routed', 'routed', 'rejected', 'all'] as const).map((s) => {
                    const active = statusFilter === s;
                    const count = data.counts[s] ?? '';
                    return (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setStatusFilter(s)}
                            style={{
                                padding: '4px 10px', borderRadius: 6,
                                border: active ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                                background: active ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                                color: active ? '#fffbeb' : 'var(--ad-ink)',
                                fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            {s === 'pending' ? t('Čeka', 'Pending')
                                : s === 'auto_routed' ? t('Auto', 'Auto')
                                : s === 'routed' ? t('Ručno', 'Routed')
                                : s === 'rejected' ? t('Odbijeno', 'Rejected')
                                : t('Sve', 'All')}
                            {count != null && count !== 0 ? ` (${count})` : ''}
                        </button>
                    );
                })}
            </div>

            {data.rows.length === 0 ? (
                <p className="muted" style={{ textAlign: 'center', padding: '1.5rem', fontStyle: 'italic' }}>
                    {t('Nema fajlova s ovim statusom.', 'No files with this status.')}
                </p>
            ) : null}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {data.rows.map((row) => {
                    const confidence = row.classifier_confidence ?? 0;
                    const guess = row.classifier_guess;
                    return (
                        <div key={row.id} style={{
                            border: '1px solid rgba(var(--ad-soft-rgb),0.25)',
                            borderRadius: 10,
                            padding: '10px 14px',
                            background: row.status === 'pending' ? 'rgba(var(--ad-acc-rgb),0.06)' : '#fff',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                <strong style={{ color: '#34383d', fontSize: '0.92rem' }}>📎 {row.filename}</strong>
                                <span className="muted" style={{ fontSize: '0.74rem' }}>{formatDateTime(row.created_at, locale)}</span>
                            </div>
                            <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
                                {row.sender_email ? <>od: <strong>{row.sender_name ?? row.sender_email}</strong> &lt;{row.sender_email}&gt;</> : null}
                                {row.subject ? <> · {row.subject}</> : null}
                                {row.size_bytes ? <> · {(row.size_bytes / 1024).toFixed(1)} KB</> : null}
                            </div>
                            {guess ? (
                                <div style={{ marginTop: 6, fontSize: '0.82rem', color: 'var(--ad-ink)' }}>
                                    🤖 {t('Klasifikator predlaže', 'Classifier suggests')}: <strong>{data.known_modules[guess] ?? guess}</strong>
                                    {' '}<span className="muted">({(confidence * 100).toFixed(0)}%)</span>
                                </div>
                            ) : null}
                            {row.company_code ? (
                                <div style={{ marginTop: 4, fontSize: '0.82rem', color: '#1f7a36' }}>
                                    🏢 {t('Firma', 'Company')}: <strong>{row.company_code}</strong> <span className="muted">— {row.company_name}</span>
                                </div>
                            ) : row.status === 'pending' ? (
                                <div style={{ marginTop: 4, fontSize: '0.82rem', color: 'var(--ad-acc-3)' }}>
                                    🏢 {t('Firma nije prepoznata — admin odabire ručno', 'Company not detected — admin picks manually')}
                                </div>
                            ) : null}
                            {row.status === 'routed' || row.status === 'auto_routed' ? (
                                <div style={{ marginTop: 6, fontSize: '0.82rem', color: '#1f7a36' }}>
                                    ✓ {t('Usmjereno', 'Routed')}: <strong>{data.known_modules[row.routed_to_module ?? ''] ?? row.routed_to_module}</strong>
                                    {row.status === 'auto_routed' ? ' ' + t('(auto)', '(auto)') : ''}
                                </div>
                            ) : null}
                            {row.import_status === 'success' ? (
                                <div style={{ marginTop: 4, fontSize: '0.78rem', color: '#1f7a36' }}>
                                    📥 {t('Import OK', 'Import OK')}{row.imported_batch_id ? ` · batch #${row.imported_batch_id}` : ''}
                                    {row.import_finished_at ? ` · ${formatDateTime(row.import_finished_at, locale)}` : ''}
                                    {/* Artisan importeri nemaju batch_id — sažetak ispisa je jedini trag koliko je redaka ušlo. */}
                                    {row.import_summary ? (
                                        <div style={{ fontSize: '0.74rem', color: 'var(--ad-ink)', whiteSpace: 'pre-wrap', marginTop: 2 }}>
                                            {row.import_summary}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {row.import_status === 'awaiting_manual' ? (
                                <div style={{ marginTop: 4, padding: '6px 10px', background: 'rgba(var(--ad-acc-rgb),0.12)', border: '1px solid rgba(var(--ad-acc-rgb),0.5)', borderRadius: 4, fontSize: '0.78rem', color: 'var(--ad-acc-3)' }}>
                                    ⚠ <strong>{t('Treba ručno pokrenuti import', 'Manual import required')}</strong>
                                    <div style={{ fontSize: '0.74rem', marginTop: 2 }}>
                                        {t(
                                            'Auto-router je usmjerio fajl, ali za ovaj modul postoji samo artisan komanda — pokreni preko terminala ili m.172. Status NIJE „uspjelo“.',
                                            'Auto-router classified the file but this module has only an artisan command — run from terminal or m.172. Status is NOT "success".',
                                        )}
                                    </div>
                                </div>
                            ) : null}
                            {row.import_status === 'failed' ? (
                                <div style={{ marginTop: 4, fontSize: '0.78rem', color: '#b14040', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                    <span>✗ {t('Import propao', 'Import failed')}: {row.import_error ?? '—'}</span>
                                    <button
                                        type="button"
                                        className="compact"
                                        onClick={async () => {
                                            try {
                                                await mutate(`/admin/ingest/inbox/${row.id}/retry`, token, 'POST');
                                                await load();
                                            } catch (e) {
                                                setError(e instanceof Error ? e.message : 'API');
                                            }
                                        }}
                                        style={{ background: 'var(--ad-acc)', color: '#fff', fontSize: '0.72rem', padding: '3px 8px', whiteSpace: 'nowrap' }}
                                    >
                                        🔁 {t('Ponovi', 'Retry')}
                                    </button>
                                </div>
                            ) : null}
                            {row.import_status === 'pending' ? (
                                <div style={{ marginTop: 4, fontSize: '0.78rem', color: 'var(--ad-acc-3)' }}>
                                    ⏳ {t('Import u tijeku…', 'Import in progress…')}
                                </div>
                            ) : null}
                            {row.status === 'rejected' ? (
                                <div style={{ marginTop: 6, fontSize: '0.82rem', color: '#b14040' }}>
                                    ✗ {t('Odbijeno', 'Rejected')}: {row.rejection_reason ?? '—'}
                                </div>
                            ) : null}
                            {row.status === 'pending' ? (
                                <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <select
                                        value={routePick[row.id] ?? guess ?? ''}
                                        onChange={(e) => setRoutePick({ ...routePick, [row.id]: e.target.value })}
                                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', fontSize: '0.82rem' }}
                                    >
                                        <option value="">— {t('Odaberi modul', 'Pick module')} —</option>
                                        {Object.entries(data.known_modules).map(([k, label]) => (
                                            <option key={k} value={k}>{label}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={companyPick[row.id] ?? row.company_id ?? ''}
                                        onChange={(e) => setCompanyPick({ ...companyPick, [row.id]: parseInt(e.target.value, 10) })}
                                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid ' + (row.company_id ? 'rgba(var(--ad-soft-rgb),0.4)' : 'rgba(var(--ad-acc-rgb),0.7)'), fontSize: '0.82rem' }}
                                    >
                                        <option value="">— {t('Odaberi firmu', 'Pick company')} —</option>
                                        {data.companies.map((c) => (
                                            <option key={c.id} value={c.id}>{c.code} · {c.name}</option>
                                        ))}
                                    </select>
                                    <button type="button" className="compact" onClick={() => void routeOne(row.id, row.company_id, row.classifier_guess)}>
                                        ✓ {t('Usmjeri', 'Route')}
                                    </button>
                                    <button type="button" className="secondary compact" onClick={() => void rejectOne(row.id)} style={{ color: '#b14040' }}>
                                        🗑 {t('Odbaci', 'Reject')}
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </>
    );
}

function RoutesTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<RoutesPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [draftModules, setDraftModules] = useState<Set<string>>(new Set());
    const [draftEvents, setDraftEvents] = useState<Set<string>>(new Set(['all']));
    const [draftRecipients, setDraftRecipients] = useState('');
    const [draftActive, setDraftActive] = useState(true);
    const [saveProgress, setSaveProgress] = useState<string>('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const r = await api<RoutesPayload>('/admin/ingest/routes', token);
            setData(r);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { void load(); }, [load]);

    async function save() {
        const recipients = draftRecipients.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
        if (recipients.length === 0) { alert(t('Unesi barem jednog primatelja.', 'Enter at least one recipient.')); return; }
        if (draftModules.size === 0) { alert(t('Odaberi barem jedan modul.', 'Pick at least one module.')); return; }
        if (draftEvents.size === 0) { alert(t('Odaberi barem jedan tip događaja.', 'Pick at least one event kind.')); return; }
        const modules = Array.from(draftModules);
        const events = Array.from(draftEvents);
        const totalRules = modules.length * events.length;
        if (totalRules > 100 && !confirm(t(`Kreirat ću ${totalRules} pravila (${modules.length} modula × ${events.length} eventa). Nastavi?`, `This creates ${totalRules} rules. Continue?`))) return;

        let done = 0; let fail = 0;
        try {
            for (const m of modules) {
                for (const ev of events) {
                    setSaveProgress(t(`Spremam ${done + 1}/${totalRules}…`, `Saving ${done + 1}/${totalRules}…`));
                    try {
                        await mutate('/admin/ingest/routes', token, 'POST', {
                            module_key: m, event_kind: ev, recipients, is_active: draftActive,
                        });
                        done++;
                    } catch {
                        fail++;
                    }
                }
            }
            setSaveProgress('');
            if (fail > 0) alert(t(`Kreirano ${done}, palo ${fail}.`, `Created ${done}, ${fail} failed.`));
            setShowAdd(false);
            setDraftModules(new Set()); setDraftEvents(new Set(['all'])); setDraftRecipients(''); setDraftActive(true);
            await load();
        } catch (e) {
            setSaveProgress('');
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    function toggleSetItem(set: Set<string>, setter: (s: Set<string>) => void, key: string) {
        const next = new Set(set);
        if (next.has(key)) next.delete(key); else next.add(key);
        setter(next);
    }

    async function remove(id: number) {
        if (!confirm(t('Obrisati ovo pravilo?', 'Delete this rule?'))) return;
        try {
            await mutate(`/admin/ingest/routes/${id}`, token, 'DELETE');
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    if (loading && !data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    return (
        <>
            <p className="muted" style={{ fontSize: '0.84rem', margin: '0 0 12px' }}>
                {t(
                    'Po modulu i tipu događaja navedi primatelje koji dobivaju obavijest. Više primatelja odvajaj zarezom ili razmakom.',
                    'Per module and event kind, list recipients who receive notifications. Separate multiple recipients with comma or space.',
                )}
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button type="button" className="compact" onClick={() => setShowAdd(true)} disabled={showAdd}>
                    + {t('Novo pravilo', 'New rule')}
                </button>
            </div>

            {showAdd ? (
                <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                    <p className="muted" style={{ fontSize: '0.78rem', margin: '0 0 10px' }}>
                        💡 {t('Klikni više modula i više event-a — sustav kreira sva pravila (cartesian product) u jednom kliku.', 'Click multiple modules and events — system creates all rules (cartesian product) in one click.')}
                    </p>

                    {/* Modul chips */}
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                            <strong style={{ fontSize: '0.82rem', color: 'var(--ad-ink)' }}>{t('Moduli', 'Modules')}</strong>
                            <span style={{ display: 'inline-flex', gap: 4 }}>
                                <button type="button" className="secondary compact" style={{ fontSize: '0.72rem', padding: '2px 8px' }} onClick={() => setDraftModules(new Set(Object.keys(data.known_modules)))}>{t('Sve', 'All')}</button>
                                <button type="button" className="secondary compact" style={{ fontSize: '0.72rem', padding: '2px 8px' }} onClick={() => setDraftModules(new Set())}>{t('Ništa', 'None')}</button>
                            </span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {Object.entries(data.known_modules).map(([k, label]) => {
                                const on = draftModules.has(k);
                                return (
                                    <button key={k} type="button" onClick={() => toggleSetItem(draftModules, setDraftModules, k)} style={{
                                        padding: '3px 9px', borderRadius: 14, fontSize: '0.76rem', cursor: 'pointer',
                                        border: '1px solid ' + (on ? '#1f7a36' : 'rgba(var(--ad-soft-rgb),0.4)'),
                                        background: on ? 'rgba(31,122,54,0.13)' : '#fff', color: on ? '#1f7a36' : '#666', fontWeight: on ? 600 : 400,
                                    }}>{on ? '✓ ' : ''}{label}</button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Event chips */}
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                            <strong style={{ fontSize: '0.82rem', color: 'var(--ad-ink)' }}>{t('Tipovi događaja', 'Event kinds')}</strong>
                            <span style={{ display: 'inline-flex', gap: 4 }}>
                                <button type="button" className="secondary compact" style={{ fontSize: '0.72rem', padding: '2px 8px' }} onClick={() => setDraftEvents(new Set(Object.keys(data.known_events)))}>{t('Sve', 'All')}</button>
                                <button type="button" className="secondary compact" style={{ fontSize: '0.72rem', padding: '2px 8px' }} onClick={() => setDraftEvents(new Set())}>{t('Ništa', 'None')}</button>
                            </span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {Object.entries(data.known_events).map(([k, label]) => {
                                const on = draftEvents.has(k);
                                return (
                                    <button key={k} type="button" onClick={() => toggleSetItem(draftEvents, setDraftEvents, k)} style={{
                                        padding: '3px 9px', borderRadius: 14, fontSize: '0.76rem', cursor: 'pointer',
                                        border: '1px solid ' + (on ? '#1e40af' : 'rgba(var(--ad-soft-rgb),0.4)'),
                                        background: on ? 'rgba(30,64,175,0.13)' : '#fff', color: on ? '#1e40af' : '#666', fontWeight: on ? 600 : 400,
                                    }}>{on ? '✓ ' : ''}{label}</button>
                                );
                            })}
                        </div>
                    </div>

                    <label className="filter-label" style={{ margin: 0, display: 'block', marginBottom: 8 }}>
                        {t('Primatelji (email-ovi)', 'Recipients (emails)')}
                        <input
                            value={draftRecipients}
                            onChange={(e) => setDraftRecipients(e.target.value)}
                            placeholder="vjakomin@gmail.com, slaven@aurodomus.eu"
                            style={{ width: '100%' }}
                        />
                    </label>
                    <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', marginTop: 4, cursor: 'pointer', fontSize: '0.86rem' }}>
                        <input type="checkbox" checked={draftActive} onChange={(e) => setDraftActive(e.target.checked)} style={{ accentColor: 'var(--ad-acc)' }} />
                        <span>{t('Aktivno', 'Active')}</span>
                    </label>
                    <div style={{ display: 'flex', gap: 6, marginTop: 14, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className="muted" style={{ fontSize: '0.78rem' }}>
                            {draftModules.size > 0 && draftEvents.size > 0
                                ? `→ ${draftModules.size * draftEvents.size} ${t('pravila bit će kreirano', 'rules will be created')}`
                                : t('Odaberi module i event tipove', 'Pick modules and event kinds')}
                            {saveProgress ? <strong style={{ marginLeft: 8, color: 'var(--ad-acc-3)' }}>{saveProgress}</strong> : null}
                        </span>
                        <span>
                            <button type="button" className="secondary compact" onClick={() => setShowAdd(false)} style={{ marginRight: 6 }}>{t('Odustani', 'Cancel')}</button>
                            <button type="button" className="compact" onClick={() => void save()} disabled={draftModules.size === 0 || draftEvents.size === 0 || !draftRecipients || saveProgress !== ''}>
                                💾 {t('Spremi sve', 'Save all')}
                            </button>
                        </span>
                    </div>
                </div>
            ) : null}

            {data.routes.length === 0 ? (
                <p className="muted" style={{ textAlign: 'center', padding: '1.5rem', fontStyle: 'italic' }}>
                    {t('Još nema pravila. Default: sve obavijesti idu na vjakomin@gmail.com (hardkodirano u kodu dok ne dodaš pravilo).', 'No rules yet. Default: all notifications go to vjakomin@gmail.com (hardcoded until you add a rule).')}
                </p>
            ) : null}

            <div className="table-wrap">
                <table className="admin-user-table">
                    <thead>
                        <tr>
                            <th>{t('Modul', 'Module')}</th>
                            <th>{t('Događaj', 'Event')}</th>
                            <th>{t('Primatelji', 'Recipients')}</th>
                            <th>{t('Aktivno', 'Active')}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.routes.map((r) => {
                            const recipients = typeof r.recipients === 'string' ? JSON.parse(r.recipients || '[]') : r.recipients;
                            return (
                                <tr key={r.id}>
                                    <td><strong>{data.known_modules[r.module_key] ?? r.module_key}</strong></td>
                                    <td>{data.known_events[r.event_kind] ?? r.event_kind}</td>
                                    <td style={{ fontSize: '0.82rem' }}>{Array.isArray(recipients) ? recipients.join(', ') : String(recipients)}</td>
                                    <td>{r.is_active ? '✓' : '✗'}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        <button type="button" className="secondary compact" onClick={() => void remove(r.id)} style={{ color: '#b14040' }}>🗑</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}
